import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Link2, Mail, CheckCircle2 } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext } from 'ra-core';
import { useState } from 'react';
import type { Vendor } from '../types';

export const VendorInviteButton = () => {
    const record = useRecordContext<Vendor>();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [inviteUrl, setInviteUrl] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    if (!record) return null;

    // Only show for vendors that are pending approval or quick_vendor
    if (record.status !== 'pending_approval' && record.status !== 'quick_vendor') {
        return null;
    }

    const handleGenerateInvite = async () => {
        if (!email || !email.includes('@')) {
            notify('Please enter a valid email address', { type: 'warning' });
            return;
        }

        setIsGenerating(true);
        try {
            // Create vendor invite
            const { data: invite } = await dataProvider.create('vendor_invites', {
                data: {
                    email,
                    vendor_id: record.id,
                    expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
                }
            });

            // Generate URL
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/vendor-onboarding?token=${invite.token}`;
            setInviteUrl(url);
            notify('Invitation link generated successfully!', { type: 'success' });
        } catch (error) {
            console.error('Error generating invite:', error);
            notify('Error generating invitation link. Please try again.', { type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopyUrl = () => {
        if (inviteUrl) {
            navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            notify('URL copied to clipboard!', { type: 'success' });
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setInviteUrl(null);
        setEmail('');
        setCopied(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                    <Mail className="w-4 h-4" />
                    Send Invite
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5" />
                        Send Vendor Invitation
                    </DialogTitle>
                    <DialogDescription>
                        Generate a secure link for {record.name} to complete their vendor profile.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {!inviteUrl ? (
                        <>
                            <div className="space-y-2">
                                <Label htmlFor="email">Vendor Email Address</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="vendor@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    defaultValue={record.email || ''}
                                />
                                <p className="text-xs text-slate-500">
                                    The invitation link will be sent to this email address.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 mb-2">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span className="font-medium">Invitation Generated!</span>
                                </div>
                                <p className="text-sm text-green-600">
                                    Share this link with the vendor. It will expire in 7 days.
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Invitation URL</Label>
                                <div className="flex gap-2">
                                    <Input
                                        readOnly
                                        value={inviteUrl}
                                        className="font-mono text-xs"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={handleCopyUrl}
                                        className="shrink-0"
                                    >
                                        {copied ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Email Template</Label>
                                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 space-y-2">
                                    <p><strong>Subject:</strong> Vendor Registration Invitation - Rovasys</p>
                                    <p className="whitespace-pre-line">
                                        Dear {record.contact_person || 'Vendor'},

                                        You have been invited to register as a vendor with Rovasys Allied Works Pvt. Ltd.

                                        Please complete your registration using the following link:
                                        {inviteUrl}

                                        This link will expire in 7 days.

                                        Best regards,
                                        Rovasys Procurement Team
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={handleClose}>
                        {inviteUrl ? 'Close' : 'Cancel'}
                    </Button>
                    {!inviteUrl && (
                        <Button 
                            onClick={handleGenerateInvite} 
                            disabled={isGenerating}
                            className="gap-2"
                        >
                            {isGenerating ? 'Generating...' : 'Generate Link'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
